'use client';

import { useTranslations } from 'next-intl';
import { type KeyboardEvent, type UIEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AgentLineChat } from '@/app/components/icons/AgentLineChat';
import { BaseLineSearch } from '@/app/components/icons/BaseLineSearch';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { Loader } from '@/app/components/ui/loader';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { extractMessageText } from '../../../_utils';
import { searchMessages } from '../../../apis';
import type { ClawMessage, ClawSearchMessageResult } from '../../../types';

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatSearchDate = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${month}.${day}`;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PREVIEW_PREFIX_CHARS = 12;
const LOAD_MORE_THRESHOLD = 160;

const buildPreviewSnippet = (text: string, query: string) => {
  const trimmedText = text.trim();
  const trimmedQuery = query.trim();
  if (!trimmedText || !trimmedQuery) return trimmedText;

  const matchIndex = trimmedText.toLowerCase().indexOf(trimmedQuery.toLowerCase());
  if (matchIndex <= PREVIEW_PREFIX_CHARS) return trimmedText;
  return `…${trimmedText.slice(matchIndex - PREVIEW_PREFIX_CHARS)}`;
};

const HighlightText = ({ query, text }: { query: string; text: string }) => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) return <>{text}</>;

  const regex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, 'ig');
  const parts = text.split(regex);
  let cursor = 0;

  return parts.map((part) => {
    if (!part) return null;

    const key = `${cursor}-${part}`;
    cursor += part.length;
    const isMatch = part.toLowerCase() === trimmedQuery.toLowerCase();
    return isMatch ? (
      <span key={key} className="text-[#5765F2]">
        {part}
      </span>
    ) : (
      <span key={key}>{part}</span>
    );
  });
};

const getMessagePreview = (message?: ClawMessage) => {
  if (!message) return '';
  return extractMessageText(message)?.content?.trim() ?? '';
};

export const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const t = useTranslations();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTokenRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ClawSearchMessageResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [contentHeight, setContentHeight] = useState<number>();
  const [nextPageToken, setNextPageToken] = useState<string>();
  const [loadingMore, setLoadingMore] = useState(false);

  const { runAsync: runSearchMessages, loading } = useFetch(
    (keyword: string, pageToken?: string) => searchMessages(keyword, pageToken),
    {
      manual: true,
    },
  );

  const normalizedQuery = query.trim();

  useEffect(() => {
    if (!open) return;

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (open) return;

    searchTokenRef.current += 1;
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
    setNextPageToken(undefined);
    setLoadingMore(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (!normalizedQuery) {
      searchTokenRef.current += 1;
      setResults([]);
      setActiveIndex(-1);
      setNextPageToken(undefined);
      setLoadingMore(false);
      return;
    }

    const currentToken = searchTokenRef.current + 1;
    searchTokenRef.current = currentToken;
    setNextPageToken(undefined);
    setLoadingMore(false);

    const timer = window.setTimeout(async () => {
      const response = await runSearchMessages(normalizedQuery);
      if (searchTokenRef.current !== currentToken) return;

      const nextResults = response.error || !response.data ? [] : (response.data.messages ?? []);
      const token = response.error ? undefined : response.data?.next_page_token?.trim() || undefined;
      setResults(nextResults);
      setActiveIndex(nextResults.length > 0 ? 0 : -1);
      setNextPageToken(token);
      setLoadingMore(false);

      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0;
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [normalizedQuery, open, runSearchMessages]);

  const handleLoadMore = async () => {
    if (!nextPageToken || loadingMore || loading) return;
    if (!normalizedQuery) return;

    const currentToken = searchTokenRef.current;
    const requestToken = nextPageToken;
    setLoadingMore(true);

    const response = await runSearchMessages(normalizedQuery, requestToken);
    if (searchTokenRef.current !== currentToken) return;

    if (response.error || !response.data) {
      setLoadingMore(false);
      return;
    }

    const more = response.data.messages ?? [];
    const newToken = response.data.next_page_token?.trim() || undefined;

    setResults((prev) => [...prev, ...more]);
    setNextPageToken(newToken === requestToken ? undefined : newToken);
    setLoadingMore(false);
  };

  const handleScrollAreaScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!nextPageToken || loadingMore || loading) return;
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - LOAD_MORE_THRESHOLD) {
      void handleLoadMore();
    }
  };

  const resultItems = useMemo(
    () =>
      results.map((item) => ({
        createdAt: item.message?.created_at,
        preview: buildPreviewSnippet(getMessagePreview(item.message as ClawMessage | undefined), normalizedQuery),
        sessionId: item.session_id ?? '',
      })),
    [normalizedQuery, results],
  );

  const isInitialLoading = loading && !loadingMore;

  const statusMessage = isInitialLoading
    ? t('claw.nav.searchLoading')
    : resultItems.length === 0
      ? normalizedQuery
        ? t('claw.nav.searchNoResults')
        : t('claw.nav.searchResults')
      : null;
  const contentStateKey = isInitialLoading
    ? 'loading'
    : resultItems.length === 0
      ? normalizedQuery
        ? 'empty'
        : 'placeholder'
      : `results-${resultItems.length}${loadingMore ? '-loading' : ''}${nextPageToken ? '-more' : ''}`;

  useLayoutEffect(() => {
    const inner = contentInnerRef.current;
    if (!inner) return;

    const target = inner.offsetHeight;

    setContentHeight((prev) => {
      if (prev === undefined) return target;
      if (prev === target) return prev;
      requestAnimationFrame(() => setContentHeight(target));
      return prev;
    });
  }, [contentStateKey]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || activeIndex < 0) return;

    const item = container.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelectResult = (sessionId: string) => {
    if (!sessionId) return;
    onOpenChange(false);
    router.push(`/claw/${sessionId}`);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!resultItems.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % resultItems.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? resultItems.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter') {
      const currentItem = resultItems[activeIndex] ?? resultItems[0];
      if (!currentItem?.sessionId) return;
      event.preventDefault();
      handleSelectResult(currentItem.sessionId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closable={false}
        inheritTheme={true}
        className="w-[700px] max-w-[calc(100vw-32px)] border-none bg-transparent p-0 shadow-none"
        overlayClassname="bg-black/72 backdrop-blur-[12px]"
      >
        <div className="rounded-[20px] border border-[rgba(255,255,255,0.16)] light:border-[rgba(0,0,0,0.08)] bg-[rgba(255,255,255,0.04)] light:bg-[rgba(255,255,255,0.92)] p-px backdrop-blur-[50px]">
          <div className="rounded-[16px] backdrop-blur-[100px]">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="mr-4 flex min-w-0 flex-1 items-center gap-2">
                <BaseLineSearch className="size-5 shrink-0 light:text-system-text05 text-system-white40" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={t('claw.nav.searchSession')}
                  className={cn(
                    'w-full min-w-0 flex-1 border-none bg-transparent p-0 font-[Poppins] text-sm leading-[1.4] outline-none',
                    'placeholder:light:text-system-text05 placeholder:text-system-white40',
                    'light:text-system-text01 text-system-white',
                  )}
                />
              </div>
              <span className="shrink-0 font-[Poppins] light:text-system-text06 text-sm text-system-white24 leading-[1.4]">
                ⌘ K
              </span>
            </div>

            <div className="h-px w-full bg-system-white08 light:bg-[rgba(0,0,0,0.08)]" />

            <div
              className="overflow-hidden"
              style={{
                height: contentHeight === undefined ? undefined : `${contentHeight}px`,
                transition: 'height 220ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <div ref={contentInnerRef}>
                {statusMessage ? (
                  <div className="flex h-[58px] items-center justify-center px-6 light:text-system-text05 text-sm text-system-white32">
                    {statusMessage}
                  </div>
                ) : (
                  <div
                    ref={scrollAreaRef}
                    onScroll={handleScrollAreaScroll}
                    className="max-h-[280px] overflow-y-auto px-3 py-4"
                  >
                    <div ref={scrollContainerRef} className="flex flex-col gap-1">
                      {resultItems.map((item, index) => {
                        const isActive = index === activeIndex;

                        return (
                          <button
                            key={`${item.sessionId}-${item.createdAt ?? ''}-${item.preview}`}
                            type="button"
                            onClick={() => handleSelectResult(item.sessionId)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={cn(
                              'flex w-full max-w-full items-center gap-4 overflow-hidden rounded-8 p-3 text-left transition-colors',
                              isActive
                                ? 'bg-[rgba(255,255,255,0.16)] light:bg-[rgba(87,101,242,0.12)]'
                                : 'hover:bg-system-white08 light:hover:bg-[rgba(0,0,0,0.04)]',
                            )}
                          >
                            <div className="flex w-0 flex-1 items-center gap-2 overflow-hidden">
                              <AgentLineChat className="size-5 shrink-0 light:text-system-text05 text-system-white40" />
                              <p className="min-w-0 flex-1 truncate text-sm text-system-white40">
                                <HighlightText query={normalizedQuery} text={item.preview} />
                              </p>
                            </div>

                            <div className="flex shrink-0 items-center gap-2 text-system-white48 text-xs">
                              <span>{formatSearchDate(item.createdAt)}</span>
                              {isActive && <span>↵</span>}
                            </div>
                          </button>
                        );
                      })}
                      {loadingMore && (
                        <div className="flex h-10 items-center justify-center">
                          <Loader className="w-8" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
